import _ from 'lodash'

/**
 * Genereert een UUID v4 string.
 * Gebruikt crypto.randomUUID() als beschikbaar (Safari 15.4+, alle moderne browsers),
 * anders een fallback op basis van Math.random().
 *
 * Waarom: crypto.randomUUID() is niet beschikbaar op Safari ≤15.3 en vereist HTTPS.
 * Zonder fallback crasht het aanmaken/bewerken van factuurregels op oudere Apple devices.
 */
export function generateUUID(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID()
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
    })
}

/**
 * Formatteert een Date object als YYYY-MM-DD in lokale tijd.
 * Gebruikt lokale datum-methodes i.p.v. toISOString() om UTC-verschuiving te voorkomen.
 *
 * Waarom: Safari parseert date-only strings ("2025-03-16") als UTC middernacht.
 * toISOString().split('T')[0] kan dan de vorige dag teruggeven in UTC+ tijdzones.
 */
export function formatLocalDate(d: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

export function capitalizeFirstLetter(field: string): string {
    return field.charAt(0).toUpperCase() + field.slice(1)
}

export function formattedDate(field: string): string {
    const [year, month, day] = field.split('-')
    return `${day}/${month}/${year}`
}

/**
 * Formatteert een Date object naar DD/MM/YYYY voor weergave.
 * Gebruik deze functie in plaats van toLocaleDateString() om
 * browser-onafhankelijke, consistente date formatting te garanderen.
 */
export function formatDisplayDate(date: Date): string {
    return formattedDate(formatLocalDate(date))
}

export function getField(item: any, field: string): any {
    return _.get(item, field, null)
}

export function numberToFixed(number: number, fractionDigits = 2): string {
    return number.toFixed(fractionDigits)
}

export function numberToLocalCurrency(number: number, local = 'nl-BE', currency = 'EUR'): string {
    return new Intl.NumberFormat('nl-BE', {
        style: 'currency',
        currency: 'EUR',
    }).format(number)
}

/**
 * Validates and normalizes number field input.
 * Handles European number format (e.g., 1.000,50 or 1.000.000,50)
 * Returns a normalized string ready for parsing.
 *
 * Gebaseerd op de beproefde from_locale logica uit het oude systeem.
 * Ondersteunt:
 * - "90.30" -> 90.30 (standaard punt decimaal)
 * - "90,30" -> 90.30 (Europees komma decimaal)
 * - "1.000,50" -> 1000.50 (Europees met duizendtallen)
 * - "1,000.50" -> 1000.50 (Amerikaans met duizendtallen)
 * - "1.000" -> 1000 (Europees duizendtal, >2 cijfers na punt)
 * - "1,000" -> 1000 (Amerikaans duizendtal)
 *
 * @param value - The value to normalize
 * @param decimals - Maximum number of decimal places (default: 2)
 * @returns Normalized string representation of the number
 */
export function validateNumberField(value: any, decimals: number = 2): string {
    let convertedValue = (value ?? 0).toString().replace(/[^0-9.,-]/g, '')

    const dotCount = (convertedValue.match(/\./g) || []).length
    const commaCount = (convertedValue.match(/,/g) || []).length

    // Case 1: Alleen één punt, geen komma -> ALTIJD decimaal (zoals oude from_locale)
    // De punt wordt altijd als decimaalteken behandeld, nooit als duizendtal-scheider.
    // Dit voorkomt ambiguïteit: "123.123" is altijd 123.123, niet 123123.
    if (dotCount === 1 && commaCount === 0) {
        // Standaard decimaal: behoud als is
        // (geen actie nodig, punt blijft decimaalteken)
    }
    // Case 2: Alleen één komma, geen punt -> komma is decimaalteken
    else if (dotCount === 0 && commaCount === 1) {
        // Komma aanwezig - converteer naar punt als decimaalteken
        convertedValue = convertedValue.replace(',', '.')
    }
    // Case 3: Beide aanwezig (1.000,50 of 1,000.50)
    else if (dotCount > 0 && commaCount > 0) {
        const lastDot = convertedValue.lastIndexOf('.')
        const lastComma = convertedValue.lastIndexOf(',')

        if (lastComma > lastDot) {
            // Europees formaat: 1.000,50 -> komma is decimaalteken
            convertedValue = convertedValue.replace(/\./g, '').replace(',', '.')
        } else {
            // Amerikaans formaat: 1,000.50 -> punt is decimaalteken
            convertedValue = convertedValue.replace(/,/g, '')
        }
    }
    // Case 4: Meerdere punten (1.000.000) - duizendtallen
    else if (dotCount > 1) {
        const thousandSeparatorPattern = /^\d{1,3}(\.\d{3})+$/
        if (thousandSeparatorPattern.test(convertedValue)) {
            convertedValue = convertedValue.replace(/\./g, '')
        } else {
            // Laatste punt is decimaal, rest zijn duizendtallen
            const parts = convertedValue.split('.')
            const decimalPart = parts.pop()
            convertedValue = parts.join('') + '.' + decimalPart
        }
    }
    // Case 5: Meerdere komma's (1,000,000) - duizendtallen
    else if (commaCount > 1) {
        const thousandSeparatorPattern = /^\d{1,3}(,\d{3})+$/
        if (thousandSeparatorPattern.test(convertedValue)) {
            convertedValue = convertedValue.replace(/,/g, '')
        } else {
            // Laatste komma is decimaal, rest zijn duizendtallen
            const parts = convertedValue.split(',')
            const decimalPart = parts.pop()
            convertedValue = parts.join('') + '.' + decimalPart
        }
    }

    if (decimals === 0) {
        convertedValue = convertedValue.replace(/\..*/g, '')
    }

    const [intPart, decPart] = convertedValue.split('.')

    if (decPart && decPart.length > decimals) {
        convertedValue = intPart + '.' + decPart.slice(0, decimals)
    }

    return convertedValue
}

/**
 * Safely parses a value to a number, handling both comma and dot as decimal separator.
 * Returns 0 for null, undefined, or empty values.
 * Returns 0 for NaN values.
 *
 * @param value - The value to parse (can be string, number, null, undefined)
 * @returns Parsed number or 0 if parsing fails
 */
export function parseNumber(value: any): number {
    if (value === null || value === undefined || value === '') {
        return 0
    }
    // Use validateNumberField to normalize the value, then parse to float
    const normalized = validateNumberField(value, 10) // Use high decimal count to preserve precision
    const parsed = parseFloat(normalized)
    return isNaN(parsed) ? 0 : parsed
}

/**
 * Rounds a number to 2 decimal places (for currency).
 * Uses Number.EPSILON to handle floating point precision issues.
 *
 * @param val - The number to round
 * @returns Number rounded to 2 decimal places
 */
export function roundCurrency(val: number): number {
    // Double-round: toFixed(6) elimineert float noise, daarna round half away from zero.
    // Bijv. 136.275 * 100 = 13627.4999... → toFixed(6) = "13627.500000" → parseFloat → round → 13628 → / 100 = 136.28
    //
    // Math.sign * Math.round(Math.abs(...)) i.p.v. Math.round(...) zodat negatieve
    // .XX5 waarden consistent met PHP afronden (weg van nul, niet richting +infinity).
    // Zonder dit: Math.round(-13627.5) = -13627, met dit: -13628 (= PHP gedrag).
    const scaled = parseFloat((val * 100).toFixed(6))
    return (Math.sign(scaled) * Math.round(Math.abs(scaled))) / 100
}

/**
 * Rounds a number to 4 decimal places (for line prices).
 * Uses double-round technique to handle IEEE 754 float precision issues.
 * Uses half-away-from-zero rounding to match PHP behavior.
 *
 * @param val - The number to round
 * @returns Number rounded to 4 decimal places
 */
export function roundLinePrice(val: number): number {
    const scaled = parseFloat((val * 10000).toFixed(6))
    return (Math.sign(scaled) * Math.round(Math.abs(scaled))) / 10000
}

export function bigNumberToFixed(number: number, fractionDigits: number): string {
    if (number >= 1000000) {
        return (number / 1000000).toFixed(fractionDigits).replace(/\.0$/, '') + 'M'
    } else if (number >= 1000) {
        return (number / 1000).toFixed(fractionDigits).replace(/\.0$/, '') + 'k'
    }
    return number.toFixed(fractionDigits)
}

export function getGraphSymbol(currentValue: any, previousValue: any, upIsBad = false): string {
    if (Array.isArray(currentValue)) {
        if (currentValue.length > 0) {
            currentValue = currentValue.reduce((a, b) => a + b, 0) / currentValue.length
        }
    }

    if (Array.isArray(previousValue)) {
        if (previousValue.length > 0) {
            previousValue = previousValue.reduce((a, b) => a + b, 0) / previousValue.length
        }
    }

    const upColor = upIsBad ? '#E02020' : '#5EA90F'
    const downColor = upIsBad ? '#5EA90F' : '#E02020'

    if (currentValue > previousValue) {
        return `<em style="color:${upColor};">▲</em>`
    }

    return `<em style="color:${downColor};">▼</em>`
}

/**
 * Hernummert place values om duplicates te voorkomen.
 * Start altijd bij 1 en incrementeert sequentieel.
 *
 * Deze functie wordt gebruikt na het toevoegen, verwijderen of verplaatsen van
 * document groepen of regels om ervoor te zorgen dat alle place values uniek
 * en sequentieel zijn (1, 2, 3, ...).
 *
 * @param elements - Array van groepen of regels met een 'place' property
 * @returns void - Muteert de input array in-place
 *
 * @example
 * const groups = [{ place: 5 }, { place: 3 }, { place: 7 }]
 * renumberPlaces(groups)
 * // groups is nu: [{ place: 1 }, { place: 2 }, { place: 3 }]
 */
export function renumberPlaces(elements: Array<{ place?: number }>): void {
    elements
        .filter((item): item is NonNullable<typeof item> => item != null)
        .forEach((item, index) => {
            item.place = index + 1
        })
}

/**
 * Escape HTML-speciale tekens om XSS te voorkomen bij v-html rendering.
 * Zie: Pentest finding - Stored XSS via Company Name
 */
export function escapeHtml(text: string | null | undefined): string {
    if (!text) return ''
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;')
}

/**
 * Converteer image data naar een weergavebare URL.
 * Ondersteunt signed URLs (http/https), SVG base64 en reguliere base64 data.
 */
export function toImageUrl(data: string | null): string | null {
    if (!data) return null
    if (data.startsWith('http://') || data.startsWith('https://')) {
        return data
    }
    // SVG base64 data begint met XML/SVG markers (PHN2Zy = <svg, PD94bW = <?xm)
    const isSvg = data.startsWith('PHN2Zy') || data.startsWith('PD94bW')
    const mimeType = isSvg ? 'image/svg+xml' : 'image/jpeg'
    return `data:${mimeType};base64,` + data
}

/**
 * Geef de vertaalsleutel voor het statuslabel van een Wings document op basis van
 * de stage in de Wings-verwerkingspijplijn.
 *
 * Probleem dat dit oplost: een geüpload Wings document stond altijd als één
 * ondoorzichtige "Te verwerken" status, terwijl het document in werkelijkheid in
 * verschillende stappen kan zitten (verzonden, in OCR-inbox, gescand, klaar om te
 * boeken). De gebruiker kon niet zien waar het document zich bevond.
 *
 * Wordt gedeeld door IndexTable.vue en MobileInfoCard.vue zodat beide exact
 * dezelfde labels tonen.
 */
export function wingsStageLabelKey(stage?: string | null): string {
    switch (stage) {
        case 'in_inbox':
            return 'status.wings_stage_in_inbox'
        case 'ocr_done':
            return 'status.wings_stage_ocr_done'
        case 'staged':
            return 'status.wings_stage_staged'
        case 'sent':
        default:
            return 'status.wings_stage_sent'
    }
}

/**
 * Geef de vertaalsleutel voor de tooltip-uitleg van een Wings stage.
 * Zie wingsStageLabelKey() voor de achtergrond.
 */
export function wingsStageTooltipKey(stage?: string | null): string {
    switch (stage) {
        case 'in_inbox':
            return 'status.wings_stage_in_inbox_tooltip'
        case 'ocr_done':
            return 'status.wings_stage_ocr_done_tooltip'
        case 'staged':
            return 'status.wings_stage_staged_tooltip'
        case 'sent':
        default:
            return 'status.wings_stage_sent_tooltip'
    }
}

/**
 * Formatteert een uploadlimiet in kilobytes naar een leesbare tekst ("10 MB", "512 KB").
 *
 * Waarom hier en niet per scherm (#6236): dezelfde omzetting stond zowel in VInput (nu VTextInput) als in
 * het announcement-formulier, en de serverkant (FileHelper::maxUploadSizeLabel) doet het
 * nog een derde keer. Drie kopieën liepen gegarandeerd uit elkaar. Het decimaalteken komt
 * uit de locale — een vaste punt of komma gaf een ander getal in de hint dan in de
 * serverfout erna.
 */
export function formatUploadSize(sizeInKb: number, locale?: string): string {
    if (sizeInKb >= 1024) {
        const rounded = Math.round((sizeInKb / 1024) * 10) / 10

        return `${rounded.toLocaleString(locale)} MB`
    }

    return `${sizeInKb} KB`
}

/**
 * Bepaalt de marker van een standaard statusfilter op admin-lijsten
 * (zie FilterHelper::applyDefaultStatusFilter in de backend).
 *
 * Waarom: de backend verbergt standaard verlopen/inactieve rijen, maar laat die
 * default vallen bij een zoekopdracht. Daarvoor moet hij weten of de status in het
 * filterpaneel nog de default is ('1') of bewust door de gebruiker gekozen ('0').
 * Blijft de selectie ongewijzigd, dan blijft het een default; elke wijziging —
 * ook "filters wissen" — maakt het een bewuste keuze.
 */
export function resolveDefaultFilterMarker(
    previousValues: string[] | null | undefined,
    nextValues: string[] | null | undefined,
    previousMarker: string | null | undefined
): '0' | '1' {
    const previous = [...(previousValues ?? [])].sort()
    const next = [...(nextValues ?? [])].sort()
    const unchanged = _.isEqual(previous, next)

    return unchanged && previousMarker !== '0' ? '1' : '0'
}

/**
 * Bevat deze sleepactie bestanden (uit Verkenner/Finder)?
 *
 * Eén definitie voor de dropzone (VFileDropzone) én de globale vangrail in app.ts: die twee
 * moeten het exact eens zijn over wat een bestands-drag is, anders blokkeert de ene
 * wat de andere nog accepteert (#6653).
 */
export function isFileDrag(event: DragEvent): boolean {
    return Array.from(event.dataTransfer?.types ?? []).includes('Files')
}
